import React, { useRef, useState } from 'react';
import Modal from '../../BaseComponents/Modal';
import './UploadModal.scss';
import { uploadMemberBloodTest } from '../../../api/oldBloodTest';
import { useLocation } from 'react-router-dom';
import BiomarkerExtractionModal from '../BiomarkerExtractionModal';
import { parseBloodReportByFilename } from '../../../api/corevitals';
import { ParsedBiomarker } from '../../../api/corevitals';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  onExtractionComplete?: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUploadSuccess,
  onExtractionComplete
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [parsedBiomarkers, setParsedBiomarkers] = useState<ParsedBiomarker[]>([]);
  const [extractionSummary, setExtractionSummary] = useState<Record<string, any> | undefined>(undefined);
  const [extractionResponseData, setExtractionResponseData] = useState<Record<string, any> | undefined>(undefined);
  const [missingList, setMissingList] = useState<Array<{ biomarker_name: string; standard_unit: string }>>([]);
  const location = useLocation();

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    const allowedMimeTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/heic',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]);

    const lowerName = file.name.toLowerCase();
    const allowedExtensions = /\.(pdf|jpe?g|png|heic|docx?)$/i;

    // Some browsers/OSes may provide empty/unknown MIME types for certain files (notably HEIC),
    // so we allow by extension as a fallback.
    const isAllowed =
      (file.type && allowedMimeTypes.has(file.type)) ||
      allowedExtensions.test(lowerName);

    if (!isAllowed) {
      setError('Please select a valid file (PDF, JPG, PNG, HEIC, DOC, DOCX)');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      const memberId = location.pathname.split('/')[2];
      if (!memberId) {
        throw new Error('Member ID not found in URL');
      }

      const uploadedData = await uploadMemberBloodTest(memberId, { file });
      
      // Only auto-extract if it's a PDF file
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        try {
          // Trigger extraction automatically
          const response = await parseBloodReportByFilename(
            uploadedData.file_name,
            uploadedData.file_path
          );

          if (response.success && ((response.data as any)?.matched_biomarkers || response.data?.biomarkers)) {
            const data = response.data as any;
            const biomarkers = data.matched_biomarkers || response.data.biomarkers || [];
            setParsedBiomarkers(biomarkers);
            setExtractionSummary(response.data.summary);
            setExtractionResponseData(response.data);
            setMissingList(data.missing_list || []);
            setIsExtractionModalOpen(true);
            // Don't close the upload modal yet, wait for extraction modal
          } else {
            // If extraction fails, just close and refresh
            onUploadSuccess();
            onClose();
          }
        } catch (extractionError: any) {
          // If extraction fails, show error message
          console.error('Extraction error:', extractionError);
          const errorMessage = extractionError?.response?.data?.error || 
                              extractionError?.message || 
                              'Failed to extract biomarkers from the report';
          setError(`Extraction failed: ${errorMessage}`);
          // Still close and refresh after showing error
          setTimeout(() => {
            onUploadSuccess();
            onClose();
          }, 3000);
        }
      } else {
        // For non-PDF files, just close and refresh
        onUploadSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error?.message || 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setError(null);
      onClose();
    }
  };

  const handleExtractionClose = () => {
    setIsExtractionModalOpen(false);
    onUploadSuccess();
    onClose();
    if (onExtractionComplete) {
      onExtractionComplete();
    }
  };

  const handleExtractionSaveSuccess = () => {
    setIsExtractionModalOpen(false);
    onUploadSuccess();
    onClose();
    if (onExtractionComplete) {
      onExtractionComplete();
    }
  };

  const memberId = location.pathname.split('/')[2];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Upload Blood Test File"
        width="600px"
      >
        <div className="upload-modal">
          <div
            className={`upload-area ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
          >
            <div className="upload-icon">
              {isUploading ? (
                <div className="loading-spinner"></div>
              ) : (
                <span>📄</span>
              )}
            </div>
            <div className="upload-text">
              {isUploading ? (
                <p className="primary-text">Uploading file...</p>
              ) : (
                <>
                  <p className="primary-text">Click to upload blood test file</p>
                  <p className="secondary-text">or drag and drop your file here</p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
        </div>
      </Modal>

      <BiomarkerExtractionModal
        isOpen={isExtractionModalOpen}
        onClose={handleExtractionClose}
        userId={memberId}
        parsedBiomarkers={parsedBiomarkers}
        summary={extractionSummary}
        responseData={extractionResponseData}
        missingList={missingList}
        onSaveSuccess={handleExtractionSaveSuccess}
      />
    </>
  );
};

export default UploadModal;
